import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ShieldCheck, DollarSign, UserCheck, LogIn, MessageSquare, MapPin, Bell, CalendarDays, ChevronRight, ArrowRight, Sun, Moon } from 'lucide-react';

const fmt = (n) => `Rp ${Number(n || 0).toLocaleString('id-ID')}`;
const MONTHS = ['','Januari','Februari','Maret','April','Mei','Juni','Juli','Agustus','September','Oktober','November','Desember'];

export default function LandingPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [darkMode, setDarkMode] = useState(() => {
        return localStorage.getItem('rt6_theme') === 'dark';
    });

    useEffect(() => {
        if (darkMode) {
            document.documentElement.classList.add('dark');
            localStorage.setItem('rt6_theme', 'dark');
        } else {
            document.documentElement.classList.remove('dark');
            localStorage.setItem('rt6_theme', 'light');
        }
    }, [darkMode]);
    const now = new Date();

    useEffect(() => {
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/landing`)
            .then(r => setData(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const regional = data?.regional || {};
    const rtLabel = regional.rt_number ? `RT ${regional.rt_number} / RW ${regional.rw_number}` : 'Sistem Informasi RT';
    const locParts = [regional.kelurahan, regional.kecamatan].filter(Boolean);
    const locLabel = locParts.join(', ');

    const ACTION_CARDS = [
        {
            id: 'keuangan',
            title: 'Laporan Keuangan',
            desc: 'Lihat ringkasan keuangan, iuran, dan pengeluaran RT secara transparan.',
            icon: DollarSign,
            href: '/public',
            color: 'from-blue-600 to-teal-600',
            bg: 'bg-blue-50',
            iconColor: 'text-blue-600',
        },
        {
            id: 'wajib-lapor',
            title: 'Wajib Lapor Tamu',
            desc: 'Laporkan kedatangan tamu ke RT untuk keamanan dan ketertiban lingkungan.',
            icon: UserCheck,
            href: '/guest-report',
            color: 'from-green-600 to-emerald-600',
            bg: 'bg-green-50',
            iconColor: 'text-green-600',
        },
        {
            id: 'portal-petugas',
            title: 'Portal Petugas RT',
            desc: 'Login untuk petugas RT dalam mengelola administrasi dan data warga.',
            icon: LogIn,
            href: '/login',
            color: 'from-[#065f46] to-[#1e40af]',
            bg: 'bg-primary/5',
            iconColor: 'text-primary',
        },
        {
            id: 'forum-warga',
            title: 'Forum Warga',
            desc: 'Akses kartu iuran digital, laporan kas, dan pengaduan warga.',
            icon: MessageSquare,
            href: '/warga',
            color: 'from-purple-600 to-violet-600',
            bg: 'bg-purple-50',
            iconColor: 'text-purple-600',
        },
    ];

    return (
        <div className="min-h-screen" style={{ background: 'hsl(210 40% 98%)' }} data-testid="landing-page">
            {/* Header */}
            <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-30 shadow-sm">
                <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        {regional.logo_url ? (
                            <img src={regional.logo_url} alt="Logo RT" className="w-9 h-9 object-contain rounded" />
                        ) : (
                            <img src="/logo_surabaya.svg" alt="Logo RT" className="w-9 h-9 object-contain" />
                        )}
                        <div className="min-w-0">
                            <h1 className="font-extrabold text-slate-800 dark:text-slate-100 text-sm sm:text-base leading-tight">
                                RT 06 / RW 01
                            </h1>
                            <p className="text-xs text-slate-400 dark:text-slate-400 font-medium leading-none mt-0.5">
                                Menanggal, Gayungan
                            </p>
                        </div>
                    </div>
                    {/* Dark/Light Mode Toggle */}
                    <button
                        onClick={() => setDarkMode(!darkMode)}
                        aria-label="Toggle Dark/Light Mode"
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all text-xs font-semibold shadow-2xs"
                    >
                        {darkMode ? (
                            <>
                                <Sun className="w-4 h-4 text-amber-400" />
                                <span className="hidden sm:inline">Terang</span>
                            </>
                        ) : (
                            <>
                                <Moon className="w-4 h-4 text-slate-600" />
                                <span className="hidden sm:inline">Gelap</span>
                            </>
                        )}
                    </button>
                </div>
            </header>

            <div className="max-w-5xl mx-auto px-4 py-8 space-y-10">
                {/* Hero / Regional Info */}
                <div className="rounded-2xl overflow-hidden shadow-sm"
                    style={{ background: 'linear-gradient(135deg, #064e3b 0%, #065f46 55%, #0f766e 100%)' }}>
                    <div className="p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center gap-6">
                        {regional.logo_url ? (
                            <img src={regional.logo_url} alt="Logo RT"
                                className="w-24 h-24 object-contain bg-white/20 rounded-2xl p-2 flex-shrink-0" />
                        ) : (
                            <img src="/logo_surabaya.svg" alt="Logo RT" className="w-24 h-24 object-contain bg-white/10 rounded-2xl p-2 flex-shrink-0" />
                        )}
                        <div className="text-white">
                            <p className="text-xs text-white/60 tracking-widest uppercase font-semibold mb-2">
                                Sistem Informasi Manajemen
                            </p>
                            <h1 className="text-3xl md:text-4xl font-extrabold leading-tight mb-3"
                                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                                {rtLabel}
                            </h1>
                            {locLabel && (
                                <p className="text-white/75 flex items-center gap-2 text-sm mb-3">
                                    <MapPin className="w-4 h-4 flex-shrink-0" /> {locLabel}
                                </p>
                            )}
                            {regional.org_photo_url && (
                                <div className="mt-4">
                                    <p className="text-xs text-white/60 mb-2">Susunan Pengurus</p>
                                    <img src={regional.org_photo_url} alt="Susunan Pengurus"
                                        className="max-h-40 rounded-xl object-contain bg-white/10 p-2" />
                                </div>
                            )}
                            <div className="mt-4 flex items-center gap-2 flex-wrap">
                                <span className="bg-white/20 backdrop-blur-sm text-white text-xs px-3 py-1 rounded-full font-medium border border-white/20">
                                    {MONTHS[now.getMonth() + 1]} {now.getFullYear()}
                                </span>
                                <span className="text-white/60 text-xs">Data real-time</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Finance quick stat */}
                {!loading && data?.finance_summary && (
                    <div className="grid grid-cols-3 gap-3">
                        {[
                            { label: 'Iuran Bulan Ini', value: fmt(data.finance_summary.iuran_total), color: 'text-green-700' },
                            { label: 'Pengeluaran', value: fmt(data.finance_summary.expense_total), color: 'text-orange-600' },
                            { label: 'Saldo Kas', value: fmt(data.finance_summary.balance), color: data.finance_summary.balance >= 0 ? 'text-teal-700' : 'text-red-600' },
                        ].map(item => (
                            <div key={item.label} className="bg-white rounded-xl border border-slate-200 p-4 text-center hover:shadow-sm transition-shadow">
                                <p className="text-xs text-slate-500 mb-1">{item.label}</p>
                                <p className={`font-extrabold text-base ${item.color}`} style={{ fontFamily: 'Plus Jakarta Sans' }}>
                                    {item.value}
                                </p>
                            </div>
                        ))}
                    </div>
                )}

                {/* Announcements */}
                {data?.announcements?.length > 0 && (
                    <div className="space-y-3">
                        <div className="flex items-center gap-2">
                            <Bell className="w-5 h-5 text-primary" />
                            <h2 className="font-bold text-slate-800 text-lg" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                                Pengumuman
                            </h2>
                        </div>
                        <div className="space-y-2">
                            {data.announcements.map(ann => (
                                <div key={ann.id} className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-sm transition-shadow" data-testid={`announcement-${ann.id}`}>
                                    <div className="flex items-start gap-3">
                                        <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                                            <Bell className="w-4 h-4 text-amber-600" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-bold text-slate-800 text-sm">{ann.title}</p>
                                            <p className="text-slate-600 text-sm mt-1 leading-relaxed">{ann.content}</p>
                                            <p className="text-xs text-slate-400 mt-2">
                                                {new Date(ann.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Action Cards */}
                <div className="space-y-3">
                    <h2 className="font-bold text-slate-800 text-lg" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                        Layanan RT
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {ACTION_CARDS.map(card => (
                            <a key={card.id} href={card.href} data-testid={`action-card-${card.id}`}
                                className="group flex items-center gap-4 bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md hover:-translate-y-0.5 transition-all">
                                <div className={`w-14 h-14 rounded-2xl ${card.bg} flex items-center justify-center flex-shrink-0`}>
                                    <card.icon className={`w-7 h-7 ${card.iconColor}`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-bold text-slate-800 text-base" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                                        {card.title}
                                    </p>
                                    <p className="text-sm text-slate-500 mt-0.5 leading-snug">{card.desc}</p>
                                </div>
                                <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-primary transition-colors flex-shrink-0" />
                            </a>
                        ))}
                    </div>
                </div>

                {/* Upcoming meetings */}
                {data?.upcoming_meetings?.length > 0 && (
                    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
                            <CalendarDays className="w-5 h-5 text-primary" />
                            <h2 className="font-bold text-slate-800" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                                Pertemuan Mendatang
                            </h2>
                        </div>
                        <ul className="divide-y divide-slate-100">
                            {data.upcoming_meetings.map((m, i) => (
                                <li key={i} className="px-5 py-4 flex items-start gap-3">
                                    <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                                        <CalendarDays className="w-4 h-4 text-purple-600" />
                                    </div>
                                    <div>
                                        <p className="font-semibold text-slate-800 text-sm">{m.title}</p>
                                        <p className="text-xs text-slate-500 mt-0.5">
                                            {m.date}{m.time ? ` · ${m.time}` : ''}{m.location ? ` · ${m.location}` : ''}
                                        </p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Footer */}
                <div className="text-center py-4">
                    <p className="text-xs text-slate-400">
                        &copy; {now.getFullYear()} Sistem Informasi RT Digital &mdash; Untuk transparansi dan kemajuan warga
                    </p>
                </div>
            </div>
        </div>
    );
}
